// Allergen and diet name matching, shared by the menu listing and search results.
//
// An item lists its allergens as free text ("Cheese, Panko, Shallot"), while the filter
// sends canonical names ("Dairy", "Gluten", "Allium"). These groups map one to the other
// so a filtered row can mark exactly the allergens the guest asked about. Ported from the
// native app's table so both clients highlight the same words.
const ALLERGEN_GROUPS = [
  {
    names: ["dairy", "milk"],
    terms: [
      "dairy", "milk", "whole milk", "skim milk", "condensed milk", "evaporated milk",
      "powdered milk", "dry milk solids", "whey", "whey protein", "whey isolate", "casein",
      "caseinate", "calcium caseinate", "sodium caseinate", "potassium caseinate", "lactose",
      "lactalbumin", "lactoglobulin", "curds", "cream", "heavy cream", "light cream",
      "sour cream", "crème fraîche", "butter", "cultured butter", "clarified butter", "ghee",
      "buttermilk", "yogurt", "kefir", "paneer", "cheese", "ricotta", "mascarpone",
      "cottage cheese", "parmesan", "mozzarella", "cheddar", "feta", "blue cheese",
      "milk solids", "milkfat", "butterfat", "milk powder", "béchamel", "alfredo"
    ]
  },
  {
    names: ["eggs", "egg"],
    terms: [
      "eggs", "egg", "whole egg", "egg white", "egg yolk", "albumin", "ovalbumin",
      "ovoglobulin", "lysozyme", "meringue", "aioli", "mayonnaise", "mayo", "hollandaise",
      "béarnaise", "custard", "egg wash", "egg noodles", "egg pasta", "lecithin (egg derived)"
    ]
  },
  {
    names: ["gluten", "wheat"],
    terms: [
      "gluten", "wheat", "wheat flour", "whole wheat", "enriched flour", "semolina", "durum",
      "farro", "spelt", "einkorn", "khorasan", "kamut", "barley", "malt", "malt syrup",
      "malt extract", "malt vinegar", "maltodextrin (wheat)", "rye", "triticale", "couscous",
      "bulgur", "seitan", "vital wheat gluten", "soy sauce", "shoyu", "panko", "breadcrumbs",
      "roux (wheat flour)", "beer", "ale", "brewer's yeast"
    ]
  },
  {
    names: ["soybeans", "soybean", "soy"],
    terms: [
      "soybeans", "soy", "soybean", "soya", "soy protein", "soy flour", "soy protein isolate",
      "soy protein concentrate", "tofu", "bean curd", "yuba", "tempeh", "edamame", "miso",
      "natto", "soy sauce", "shoyu", "tamari", "hoisin", "teriyaki", "vegetable protein",
      "hydrolyzed vegetable protein", "textured vegetable protein", "tvp", "lecithin (soy)",
      "vegetable oil (soy)"
    ]
  },
  {
    names: ["sesame"],
    terms: [
      "sesame", "sesame seeds", "white sesame", "black sesame", "toasted sesame", "sesame oil",
      "tahini", "sesame paste", "halvah", "gomasio", "benne", "benne seed", "benne oil",
      "simsim", "til"
    ]
  },
  {
    names: ["tree nuts", "treenuts", "tree nut"],
    terms: [
      "almond", "almond flour", "almond milk", "almond butter", "almond paste", "marzipan",
      "cashew", "cashew cream", "cashew butter", "pistachio", "pecan", "walnut", "hazelnut",
      "filbert", "macadamia", "brazil nut", "pine nut", "pignoli", "pignolia", "chestnut",
      "gianduja", "nougat", "frangipane", "nut butter", "nut oils"
    ]
  },
  {
    names: ["peanuts", "peanut"],
    terms: [
      "peanuts", "peanut", "groundnut", "monkey nut", "peanut butter", "peanut flour",
      "peanut oil (unrefined)", "boiled peanuts", "peanut sauce", "satay sauce", "arachis oil",
      "arachis"
    ]
  },
  {
    names: ["fish"],
    terms: [
      "salmon", "trout", "tuna", "halibut", "cod", "haddock", "pollock", "hake", "bass",
      "snapper", "mackerel", "sardine", "anchovy", "anchovy paste", "bonito", "swordfish",
      "grouper", "catfish", "sole", "tilapia", "whitefish", "monkfish", "fish sauce",
      "nuoc mam", "nam pla", "worcestershire sauce", "caesar dressing", "dashi",
      "bonito flakes", "surimi", "imitation crab", "fish gelatin", "isinglass", "fish stock",
      "seafood broth", "fish oil", "raw fish"
    ]
  },
  {
    names: ["crustacean shellfish"],
    terms: [
      "shellfish", "shrimp", "prawn", "scampi", "langoustine", "lobster", "crab", "king crab",
      "snow crab", "stone crab", "blue crab", "dungeness crab", "soft shell crab", "crayfish",
      "crawfish", "crawdad", "krill", "shellfish stock", "seafood stock", "shrimp paste",
      "prawn paste", "belacan", "bagoong", "gumbo base (shellfish)", "crab extract",
      "lobster extract", "crustacean extract", "shellfish powder", "shrimp broth", "crab broth",
      "lobster broth", "seafood bouillon"
    ]
  },
  {
    names: ["pork"],
    terms: [
      "pork", "pig", "hog", "swine", "bacon", "pancetta", "guanciale", "lard", "leaf lard",
      "ham", "prosciutto", "jamón", "jamón serrano", "jamón ibérico", "speck", "country ham",
      "city ham", "smoked ham", "sausage (pork)", "pork sausage", "breakfast sausage",
      "chorizo (pork)", "andouille", "bratwurst (pork)", "salami", "pepperoni", "mortadella",
      "coppa", "capicola", "porchetta", "pork belly", "salt pork", "ribs (pork)", "spare ribs",
      "baby back ribs", "pulled pork", "pork shoulder", "pork butt", "boston butt", "pork loin",
      "pork chop", "pork tenderloin", "ground pork", "minced pork", "lardo", "cracklings",
      "pork rinds", "chicharrón", "gelatin (pork)", "pork gelatin", "enzymes (porcine)",
      "porcine enzymes", "natural casings (pork)", "meat stock (pork)", "pork stock",
      "pork broth", "fatback", "salted fatback"
    ]
  },
  {
    names: ["allium"],
    terms: [
      "allium", "onion", "yellow onion", "white onion", "red onion", "sweet onion", "shallot",
      "scallion", "green onion", "spring onion", "leek", "pearl onion", "cipollini",
      "boiling onion", "garlic", "roasted garlic", "black garlic", "chive", "garlic chive",
      "chinese chive", "onion powder", "garlic powder", "onion granules", "garlic granules",
      "onion salt", "garlic salt", "garlic paste", "ginger-garlic paste", "onion puree",
      "garlic puree", "garlic oil", "onion oil", "allium oil", "garlic butter", "garlic aioli",
      "sofrito", "mirepoix", "vegetable stock (onion)", "broth (onion)", "caramelized onion",
      "fried onion", "crispy onion", "pickled onion", "garlic sauce", "garlic spread",
      "dehydrated onion", "dehydrated garlic"
    ]
  }
]

function normalize(text) {
  return String(text ?? "").trim().toLowerCase()
}

// The backend's helper.DIET_GROUPS: groups naming what an item is, not what it contains.
export const DIET_GROUPS = ["Vegan", "Vegetarian"]

// The backend's VEGAN_TERMS / VEGETARIAN_TERMS; a vegan item is vegetarian too.
const VEGAN_LABELS = ["vegan", "vegan-friendly", "plant-based", "plant based", "vg", "ve"]
const VEGETARIAN_LABELS = ["vegetarian", "vegetarian-friendly", "veggie", "v", ...VEGAN_LABELS]

const DIET_LABELS = new Map([
  ["vegan", VEGAN_LABELS],
  ["vegetarian", VEGETARIAN_LABELS]
])

export function isDietGroup(name) {
  return DIET_LABELS.has(normalize(name))
}

// Port of the backend's allergen_tag_variants: the tag, its head before a bracket, names inside.
function tagForms(entry) {
  const written = normalize(entry)
  if (!written) return new Set()
  const forms = new Set([written])

  const base = written.split(/[(:]/)[0].replace(/^[ \-–—,]+|[ \-–—,]+$/g, "")
  if (base) forms.add(base)

  for (const [, inside] of written.matchAll(/\(([^)]*)\)/g)) {
    for (const piece of inside.split(/[/,]/)) {
      const name = piece.trim()
      if (name && name.split(/\s+/).length <= 2) forms.add(name)
    }
  }
  return forms
}

// Whole entries only: "Vegan" and "Legume (vegan)" match; "Not Vegan", "can be made vegan" never.
function isDietLabel(entry, labels) {
  const forms = tagForms(entry)
  return labels.some((label) => forms.has(label))
}

// Each chip toggles on its own; diets combine with allergens and with each other, as on iOS.
export function toggleFilter(selectedIds, id) {
  if (selectedIds.includes(id)) return selectedIds.filter((entry) => entry !== id)
  return [...selectedIds, id]
}

// The diet when it is the whole selection; a diet mixed with other filters reads as allergens.
function soleDiet(selectedIds) {
  const named = selectedIds.filter((id) => normalize(id))
  if (named.length !== 1) return null
  return DIET_GROUPS.find((group) => normalize(group) === normalize(named[0])) ?? null
}

export function isDietSelection(selectedIds) {
  return soleDiet(selectedIds) !== null
}

// Include / exclude radio labels: "Vegan" / "Not Vegan" for a diet alone, else "With Allergens".
export function filterModeLabels(selectedIds) {
  const diet = soleDiet(selectedIds)
  if (!diet) return { including: "With Allergens", excluding: "Without Allergens" }
  return { including: diet, excluding: `Not ${diet}` }
}

// Matches a group by exact name first, then by partial name, as the native app does —
// "Peanut" and "Peanuts" have to land on the same group.
function groupFor(selected) {
  return (
    ALLERGEN_GROUPS.find((group) => group.names.includes(selected)) ??
    ALLERGEN_GROUPS.find((group) =>
      group.names.some((name) => name.includes(selected) || selected.includes(name))
    ) ??
    null
  )
}

// True when an allergen listed on an item is one the guest filtered for, directly or
// through a group term ("Béchamel" counts as Dairy), or labels the selected diet.
export function isAllergenMatch(allergen, selectedNames) {
  const candidate = normalize(allergen)
  if (!candidate) return false

  return selectedNames.map(normalize).filter(Boolean).some((selected) => {
    const dietLabels = DIET_LABELS.get(selected)
    if (dietLabels) return isDietLabel(candidate, dietLabels)
    if (candidate === selected) return true
    if (groupFor(selected)?.terms.includes(candidate)) return true
    // The item can also name the group itself while the filter names a member.
    const candidateGroup = ALLERGEN_GROUPS.find((group) => group.names.includes(candidate))
    return candidateGroup?.terms.includes(selected) === true
  })
}

export function splitAllergens(value) {
  return String(value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
}

// Matches lead the line so the allergen a guest filtered for is the first thing read.
export function orderByMatch(allergens, selectedNames) {
  const matched = allergens.filter((entry) => isAllergenMatch(entry, selectedNames))
  const rest = allergens.filter((entry) => !isAllergenMatch(entry, selectedNames))
  return [...matched, ...rest]
}
